/**
 * nav-config API client (IA-Navigation Phase 3).
 *
 * Deliberately uses raw axios (not the shared `apiClient`) so the global
 * error-toast interceptor does NOT fire on failure: the nav config is loaded in
 * the background on every app start and may 503 until the Cosmos container is
 * provisioned (run 074). Both calls are best-effort — failures resolve to
 * null/false and the caller falls back to the localStorage cache / bundled
 * default.
 */
import axios from 'axios';
import { NavConfig, Perspective } from '../nav/navTypes';

const NAV_CONFIG_URL = '/api/nav-config';
const PERSPECTIVE_KEYS: Perspective[] = ['tutor', 'admin', 'betrieb'];

function isNavConfig(value: unknown): value is NavConfig {
  if (!value || typeof value !== 'object') return false;
  const cfg = value as Record<string, { groups?: unknown; tray?: unknown } | undefined>;
  return PERSPECTIVE_KEYS.every((k) => {
    const pn = cfg[k];
    return !!pn && Array.isArray(pn.groups) && Array.isArray(pn.tray);
  });
}

/** GET the global config. Returns null on any failure or a non-conforming body. */
export async function getNavConfigFromApi(): Promise<NavConfig | null> {
  try {
    const res = await axios.get(NAV_CONFIG_URL);
    const data = (res?.data?.data ?? null) as unknown;
    return isNavConfig(data) ? data : null;
  } catch {
    return null;
  }
}

/** PUT the global config (admin only server-side). Best-effort; returns success. */
export async function putNavConfigToApi(config: NavConfig): Promise<boolean> {
  try {
    await axios.put(NAV_CONFIG_URL, config);
    return true;
  } catch {
    return false;
  }
}
